import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  CheckCircle2,
  XCircle,
  Download,
  Mail,
  Printer,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import Confetti from 'react-confetti';
import CeCertificatePreview from './CeCertificatePreview';

/**
 * CeAssessmentResults - Results screen with pass/fail states, confetti, and question review
 * 
 * Pixel-perfect port from Polymet prototype with succession theme variables.
 * BUG FIX: Changed incorrect useState for confetti trigger to useEffect.
 */
const CeAssessmentResults = ({
  passed,
  score,
  passingThreshold,
  totalQuestions,
  correctAnswers,
  creditHours,
  courseName,
  completionDate,
  onDownloadCertificate,
  onEmailCertificate,
  onRetry,
  onContinue,
  questionResults = [],
  userName = 'Advisor',
  sponsorName,
  certificateId,
  className = '',
}) => {
  const intl = useIntl();
  const [showDetails, setShowDetails] = useState(false);
  const [triggerConfetti, setTriggerConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  // BUG FIX: Use useEffect instead of useState for confetti trigger
  // The Polymet prototype incorrectly used useState(() => {...})
  useEffect(() => {
    if (passed) {
      const timer = setTimeout(() => setTriggerConfetti(true), 300);
      return () => clearTimeout(timer);
    }
  }, [passed]);

  // Get window dimensions for confetti
  useEffect(() => {
    const updateSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Confetti Effect */}
      {passed && triggerConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
          colors={['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899']}
        />
      )}

      {/* Results Header Card */}
      <div className="border border-succession-border rounded-xl bg-succession-bg-primary text-center">
        {/* Inner: p-12 (48px all sides) */}
        <div className="p-12 space-y-6">
          {/* Pass/Fail Icon: w-20 h-20 (80x80px), icon w-12 h-12 (48x48px) */}
          <div className="flex justify-center">
            {passed ? (
              <div className="w-20 h-20 rounded-full bg-succession-success/10 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-succession-success" />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-succession-error/10 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-succession-error" />
              </div>
            )}
          </div>

          {/* Headline */}
          <div className="space-y-2">
            {passed ? (
              <>
                <h2 className="text-3xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.ce_results_congratulations' })}</h2>
                <p className="text-xl text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.plans.ce_results_earned_credit' }, { hours: creditHours })}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.ce_results_keep_learning' })}</h2>
                <p className="text-xl text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.plans.ce_results_on_right_track' })}
                </p>
              </>
            )}
          </div>

          {/* Score Display: text-5xl font-bold (48px) */}
          <div className="space-y-2">
            <p className="text-5xl font-bold text-succession-text-primary">{Math.round(score)}%</p>
            <p className="text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.plans.ce_results_questions_correct' }, { correct: correctAnswers, total: totalQuestions })}
            </p>
            {!passed && (
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.plans.ce_results_need_to_pass' }, { threshold: passingThreshold })}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-3 pt-4">
            {passed ? (
              <button
                onClick={onContinue}
                className="px-6 py-3 text-lg font-medium text-white bg-succession-primary hover:bg-succession-primary/90 rounded-lg transition-all duration-300"
              >
                {intl.formatMessage({ id: 'succession.plans.ce_results_continue' })}
              </button>
            ) : (
              <>
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="px-6 py-3 text-lg font-medium text-white bg-succession-primary hover:bg-succession-primary/90 rounded-lg transition-all duration-300"
                  >
                    {intl.formatMessage({ id: 'succession.plans.ce_results_try_again' })}
                  </button>
                )}
                <button
                  onClick={onContinue}
                  className="px-6 py-3 text-lg font-medium text-succession-text-primary border border-succession-border hover:bg-succession-hover-bg rounded-lg transition-all duration-300"
                >
                  {intl.formatMessage({ id: 'succession.plans.ce_results_review_material' })}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Certificate Preview (Pass Only) */}
      {passed && certificateId && (
        <div className="space-y-4">
          {/* Header: flex items-center justify-between */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.ce_results_your_certificate' })}</h3>
            {/* Button spacing: gap-2 */}
            <div className="flex items-center gap-2">
              {onDownloadCertificate && (
                <button
                  onClick={onDownloadCertificate}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-succession-text-secondary border border-succession-border hover:bg-succession-hover-bg rounded-lg transition-all duration-300"
                >
                  <Download className="w-4 h-4" />
                  {intl.formatMessage({ id: 'succession.plans.ce_results_download' })}
                </button>
              )}
              {onEmailCertificate && (
                <button
                  onClick={onEmailCertificate}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-succession-text-secondary border border-succession-border hover:bg-succession-hover-bg rounded-lg transition-all duration-300"
                >
                  <Mail className="w-4 h-4" />
                  {intl.formatMessage({ id: 'succession.plans.ce_results_email' })}
                </button>
              )}
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm text-succession-text-secondary border border-succession-border hover:bg-succession-hover-bg rounded-lg transition-all duration-300"
              >
                <Printer className="w-4 h-4" />
                {intl.formatMessage({ id: 'succession.plans.ce_results_print' })}
              </button>
            </div>
          </div>

          <CeCertificatePreview
            userName={userName}
            courseName={courseName}
            creditHours={creditHours}
            completionDate={completionDate}
            sponsorName={sponsorName}
            certificateId={certificateId}
          />
        </div>
      )}

      {/* Question Results */}
      {questionResults.length > 0 && (
        <div className="border border-succession-border rounded-xl bg-succession-bg-primary">
          {/* Outer: p-6 */}
          <div className="p-6 space-y-4">
            {/* Toggle button: w-full flex items-center justify-between text-left */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full flex items-center justify-between text-left"
            >
              <h3 className="text-lg font-semibold text-succession-text-primary">
                {passed ? intl.formatMessage({ id: 'succession.plans.ce_results_review_answers' }) : intl.formatMessage({ id: 'succession.plans.ce_results_questions_missed' })}
              </h3>
              {showDetails ? (
                <ChevronUp className="w-5 h-5 text-succession-text-secondary" />
              ) : (
                <ChevronDown className="w-5 h-5 text-succession-text-secondary" />
              )}
            </button>

            {showDetails && (
              <div className="space-y-4 pt-2">
                {questionResults
                  .filter((result) => !passed || !result.isCorrect)
                  .map((result, idx) => (
                    <div
                      key={idx}
                      className="p-4 bg-succession-bg-secondary/50 rounded-lg space-y-2"
                    >
                      {/* Result items: flex items-start gap-3, icon has mt-0.5 flex-shrink-0 */}
                      <div className="flex items-start gap-3">
                        {result.isCorrect ? (
                          <CheckCircle2 className="w-5 h-5 text-succession-success mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-succession-error mt-0.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 space-y-2">
                          <p className="font-medium text-succession-text-primary">
                            {result.questionText || result.question}
                          </p>
                          <div className="text-sm space-y-1">
                            <p className="text-succession-text-secondary">
                              {intl.formatMessage({ id: 'succession.plans.ce_results_your_answer' })}{' '}
                              <span
                                className={
                                  result.isCorrect
                                    ? 'text-succession-success'
                                    : 'text-succession-error'
                                }
                              >
                                {result.selectedOptionText || result.userAnswer}
                              </span>
                            </p>
                            {!result.isCorrect && (
                              <p className="text-succession-text-secondary">
                                {intl.formatMessage({ id: 'succession.plans.ce_results_correct_answer' })}{' '}
                                <span className="text-succession-success">
                                  {result.correctOptionText || result.correctAnswer}
                                </span>
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

CeAssessmentResults.propTypes = {
  passed: PropTypes.bool.isRequired,
  score: PropTypes.number.isRequired,
  passingThreshold: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  correctAnswers: PropTypes.number.isRequired,
  creditHours: PropTypes.number.isRequired,
  courseName: PropTypes.string.isRequired,
  completionDate: PropTypes.string.isRequired,
  onDownloadCertificate: PropTypes.func,
  onEmailCertificate: PropTypes.func,
  onRetry: PropTypes.func,
  onContinue: PropTypes.func.isRequired,
  questionResults: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      questionText: PropTypes.string,
      userAnswer: PropTypes.string,
      selectedOptionText: PropTypes.string,
      correctAnswer: PropTypes.string,
      correctOptionText: PropTypes.string,
      isCorrect: PropTypes.bool,
    })
  ),
  userName: PropTypes.string,
  sponsorName: PropTypes.string,
  certificateId: PropTypes.string,
  className: PropTypes.string,
};

CeAssessmentResults.defaultProps = {
  onDownloadCertificate: null,
  onEmailCertificate: null,
  onRetry: null,
  questionResults: [],
  userName: 'Advisor',
  sponsorName: null,
  certificateId: null,
  className: '',
};

export default CeAssessmentResults;

